import React from 'react';
import { useTenantContext } from '../../../contexts/TenantContext';
import { TenantListTable } from '../../../components/TenantList';
import { apiUrl } from '../../../utils/apiUrl';
import { TenantData, TenantSearchableResponse } from '../../../App.types';
import { useTranslation } from 'react-i18next';
import useTenantListApi from '../../../hooks/useTenantListApi';
import { useJobExecutionContext } from '../../../contexts/JobExecutionContext';
import { useInstanceInfoMap } from 'hooks/useInstanceInfoMap';

export const TenantChildren: React.FC = (props) => {
  const { t } = useTranslation();
  const { tenant, loading, error } = useTenantContext();
  const { learnInstance } = useJobExecutionContext();

  const isLearn = tenant?.tenantType === 'Learn';

  const {
    data,
    error: childError,
    loading: loadingChildren,
    fetch,
  } = useTenantListApi<TenantSearchableResponse>(apiUrl('tenancy', 'tenants/searchable'));

  React.useEffect(() => {
    fetch().catch(() => {});
  }, [fetch]);
  const childrenIds = isLearn ? learnInstance?.tenantChildren : tenant?.tenantChildren;

  const tenants = React.useMemo<TenantData[]>(
    () => ((data?.results ?? []) as TenantData[]).filter((t) => childrenIds?.includes(t.id)),
    [data, childrenIds],
  );

  // Fetch instance info (installationId/url/learnVersion/band/instanceClass) for every Learn child
  // up front, in one wave, instead of per-row on demand - so the whole list is searchable/sortable
  // by those fields, not just the ones currently visible.
  const learnTenantIds = React.useMemo(
    () => tenants.filter((t) => t.tenantType === 'Learn').map((t) => t.id),
    [tenants],
  );
  const { data: instanceInfoByTenantId, loading: loadingInstanceInfo } =
    useInstanceInfoMap(learnTenantIds);

  return (
    <TenantListTable
      tenants={tenants}
      loading={loading || loadingChildren || loadingInstanceInfo}
      error={error ?? childError}
      searchText={t('tenantList.searchAssets')}
      columns={[
        'installationId',
        'url',
        'name',
        'tenantType',
        'learnVersion',
        'status',
        'region',
        'fleet',
        'band',
      ]}
      instanceInfoByTenantId={instanceInfoByTenantId}
      {...props}
    />
  );
};
